import { toast, Bounce } from "react-toastify";

/**
 * Shows an error snackbar with an optional stacktrace ID and instance that can be copied
 * @param message - The error message to display
 * @param stacktraceId - Optional error ID/trace ID that users can copy for support
 * @param instance - Optional instance information (endpoint path, etc.)
 */
export function showErrorSnackbar(message: string, stacktraceId?: string, instance?: string) {
  const copyToClipboard = async (text: string) => {
    const legacyDocument = document as unknown as {
      execCommand?: (commandId: string, showUi?: boolean, value?: string) => boolean;
    };

    const reportClipboardError = (context: string, error: unknown) => {
      console.warn(`Clipboard operation failed (${context})`, error);
    };

    try {
      await navigator.clipboard.writeText(text);
      toast.success("Error details copied to clipboard!", { autoClose: 2000 });
    } catch (error) {
      reportClipboardError('primary copy', error);
      // Fallback for older browsers or when clipboard API is not available
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        // Create a temporary selection and try to copy
        const range = document.createRange();
        range.selectNodeContents(textArea);
        const selection = globalThis.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);

        const fallbackCopySucceeded = legacyDocument.execCommand?.('copy') ?? false;
        selection?.removeAllRanges();
        
        textArea.remove();

        if (fallbackCopySucceeded) {
          toast.success("Error details copied to clipboard!", { autoClose: 2000 });
          return;
        }
        
        // Show instruction for manual copy since automated copy failed
        toast.info(`Please copy manually: ${text}`, { 
          autoClose: 6000,
          onClick: async () => {
            // Allow user to click toast to try clipboard API again
            try {
              if (!navigator.clipboard) {
                return;
              }
              await navigator.clipboard.writeText(text);
              toast.success("Copied to clipboard!", { autoClose: 2000 });
            } catch (retryError) {
              reportClipboardError('retry copy', retryError);
              // If still fails, at least the text is visible in the toast
            }
          }
        });
      } catch (fallbackError) {
        reportClipboardError('fallback copy', fallbackError);
        // Last resort: show the text for manual copying
        toast.info(`Copy this error info: ${text}`, { autoClose: 8000 });
      }
    }
  };

  toast.error(
    ({ closeToast: _ }) => (
      <div>
        <div>{message}</div>
        {(stacktraceId || instance) && (
          <div style={{ 
            marginTop: "8px",
            padding: "6px 8px",
            backgroundColor: "#f5f5f5",
            border: "1px solid #ddd",
            borderRadius: "4px",
            fontSize: "0.8em",
            fontFamily: "monospace"
          }}>
            <div style={{ 
              fontSize: "0.75em", 
              color: "#666", 
              marginBottom: "2px",
              textTransform: "uppercase",
              fontWeight: "bold"
            }}>
              📋 Error Details (Click to copy for support)
            </div>
            <button 
              style={{ 
                background: "none",
                border: "none",
                padding: "0",
                cursor: "pointer",
                color: "#333",
                userSelect: "all",
                wordBreak: "break-all",
                textAlign: "left",
                width: "100%",
                fontFamily: "inherit",
                fontSize: "inherit"
              }}
              onClick={() => {
                const errorDetails = [
                  stacktraceId && `Trace ID: ${stacktraceId}`,
                  instance && `Instance: ${instance}`
                ].filter(Boolean).join('\n');
                copyToClipboard(errorDetails);
              }}
              aria-label="Copy error details to clipboard"
              title="Click to copy error details"
            >
              {stacktraceId && <div>Trace ID: {stacktraceId}</div>}
              {instance && <div>Instance: {instance}</div>}
            </button>
          </div>
        )}
      </div>
    ),
    {
      position: "top-right",
      autoClose: 8000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: true,
      theme: "light",
      transition: Bounce,
    }
  );
}
